/**
 * EventsTab — reading events / book club meetups + reading challenges.
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users, Check, Trophy, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button, cx } from '@/components/site/primitives';
import { fadeUp, stagger } from '@/lib/motion';
import { toast } from '@/components/ui/sonner';
import { useCommunities } from '../context/CommunitiesContext';
import { useModalState } from '../hooks';
import { formatEventDate, formatCount } from '../utils/communityHelpers';
import EmptyState from '../EmptyState';
import BookCoverArt from '@/components/books/BookCoverArt';

function EventCard({ event }) {
  const { toggleEventJoin } = useCommunities();
  return (
    <div className="card-paper flex flex-col gap-3 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-lg font-semibold leading-snug text-ink">📚 {event.title}</p>
          {event.book && <p className="mt-0.5 text-sm text-subtle">{event.book.title}</p>}
        </div>
        {event.book && <BookCoverArt book={event.book} className="h-12 w-8 shrink-0 shadow-card" />}
      </div>
      <p className="flex items-center gap-1.5 text-sm text-subtle"><Calendar className="h-3.5 w-3.5" /> {formatEventDate(event.datetime)}</p>
      <p className="flex items-center gap-1.5 text-xs text-subtle"><Users className="h-3.5 w-3.5" /> {formatCount(event.interested)} people interested</p>
      <button
        type="button" onClick={() => { toggleEventJoin(event.id); toast(event.joined ? 'Left event' : `You're in for “${event.title}”`); }}
        className={cx(
          'mt-1 inline-flex w-fit items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-colors',
          event.joined ? 'border border-coral/40 bg-coral/10 text-coral' : 'bg-coral-fill text-[#FFF7F0] hover:-translate-y-0.5'
        )}
      >
        {event.joined ? <><Check className="h-3.5 w-3.5" /> Going</> : 'Join Event'}
      </button>
    </div>
  );
}

function ChallengeCard({ challenge }) {
  const pct = Math.min(100, Math.round((challenge.progress / challenge.goal) * 100));
  return (
    <div className="card-paper p-5">
      <div className="flex items-center gap-2">
        <Trophy className="h-4 w-4 text-yellow" />
        <p className="font-display text-lg font-semibold text-ink">{challenge.title}</p>
      </div>
      <p className="mt-1.5 text-sm text-subtle">{challenge.description}</p>
      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between text-xs font-medium text-subtle">
          <span>Your progress</span><span className="text-coral">{challenge.progress}/{challenge.goal}</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-line/70">
          <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="h-full rounded-full bg-gradient-to-r from-coral to-yellow" />
        </div>
      </div>
      <p className="mt-3 text-xs text-subtle">{formatCount(challenge.participantCount)} readers participating</p>
      <div className="mt-3 space-y-1.5 border-t border-line pt-3">
        {challenge.leaderboard.map((entry, i) => (
          <div key={entry.name} className="flex items-center justify-between text-xs">
            <span className={cx('font-medium', entry.name === 'You' ? 'text-coral' : 'text-ink')}>{i + 1}. {entry.name}</span>
            <span className="text-subtle">{entry.count} books</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateEventModal({ isOpen, onClose, community }) {
  const { createEvent } = useCommunities();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [bookId, setBookId] = useState('');
  const [date, setDate] = useState('');
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!title.trim() || !date) return setError('Title and event date are required.');
    setError('');
    try {
      await createEvent(community.id, {
        title: title.trim(),
        description: description.trim(),
        bookId: bookId.trim(),
        eventAt: new Date(date).toISOString(),
      });
      toast(`Created “${title}”`);
      setTitle(''); setDescription(''); setBookId(''); setDate('');
      onClose();
    } catch (eventError) {
      setError(eventError.message || 'We could not create this event.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[92vw] max-w-sm rounded-2xl border-line bg-card p-6 text-ink">
        <DialogTitle className="font-display text-xl font-semibold text-ink">Create an Event</DialogTitle>
        <div className="mt-4 space-y-3">
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Sunday Book Club" className="rounded-xl border-line bg-cream" />
          <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Event description" className="rounded-xl border-line bg-cream" />
          <Input value={bookId} onChange={e => setBookId(e.target.value)} placeholder="Book ID (optional)" className="rounded-xl border-line bg-cream" />
          <Input type="datetime-local" value={date} onChange={e => setDate(e.target.value)} className="rounded-xl border-line bg-cream" />
        </div>
        {error && <p className="mt-3 rounded-lg bg-coral/10 px-3 py-2 text-xs font-medium text-coral">{error}</p>}
        <div className="mt-5 flex justify-end gap-2.5">
          <Button variant="secondary" icon={false} onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={false} onClick={handleCreate}>Create Event</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function EventsTab({ community }) {
  const { getEvents, getChallenge, loadCommunityEvents } = useCommunities();
  const [isCreateOpen, openCreate, closeCreate] = useModalState(false);
  const events = getEvents(community.id).sort((a, b) => a.datetime - b.datetime);
  const challenge = getChallenge(community.id);

  useEffect(() => {
    loadCommunityEvents(community.id).catch((error) => toast(error.message || 'We could not load community events.'));
  }, [community.id, loadCommunityEvents]);

  return (
    <div className="u-container max-w-3xl py-8">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-ink">Upcoming Events</h2>
        <button type="button" onClick={openCreate} className="inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-xs font-semibold text-ink hover:border-coral/40 hover:text-coral">
          <Plus className="h-3.5 w-3.5" /> Create Event
        </button>
      </div>

      {events.length === 0 ? (
        <EmptyState type="events" onCTAClick={openCreate} />
      ) : (
        <motion.div variants={stagger} initial="hidden" animate="show" className="mt-5 grid gap-4 sm:grid-cols-2">
          {events.map((e, i) => <motion.div key={e.id} variants={fadeUp} custom={i}><EventCard event={e} /></motion.div>)}
        </motion.div>
      )}

      {challenge && (
        <div className="mt-10">
          <h2 className="font-display text-xl font-semibold text-ink">Reading Challenge</h2>
          <div className="mt-5 max-w-md"><ChallengeCard challenge={challenge} /></div>
        </div>
      )}

      <CreateEventModal isOpen={isCreateOpen} onClose={closeCreate} community={community} />
    </div>
  );
}
